/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import colorUtils from 'color';

import { hover } from 'app/utils/cssUtils';
import Tappable from 'app/components/commons/Tappable';
import StarSymbol from 'app/components/challenge/StarSymbol';
import AnimateNumber from 'app/components/commons/AnimateNumber';
import PlayerLabels from '../../../components/challenge/PlayerLabels';

type Props = {
  disabled?: boolean,
  isScratched?: boolean,
  dropped?: boolean,
  isTeam?: boolean,
  labels?: Array<*>,
  name: string,
  onSelectEntity: (entity: Object) => void,
  win: number,
  place: number,
  scheme?: 'light' | 'dark' | 'white',
  isSelected?: boolean
};

const EntityWrapper = styled.div(
  ({ theme, isSelected }) => ({
    fontSize: '0.9rem',
    color: isSelected ? theme.color.white : theme.color.basis1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 0,
    borderBottomWidth: 1,
    padding: theme.spacing.tiny
  }),
  ({ theme, scheme }) =>
    ({
      white: {
        border: `1px solid ${theme.color.basis4}`
      },
      light: {
        backgroundColor: theme.color.panel,
        border: `1px solid ${theme.color.basis4}`
      },
      dark: {
        backgroundColor: theme.color.panel,
        border: `1px solid ${theme.color.basis4}`
      }
    }[scheme]),
  ({ disabled, theme, isSelected }) =>
    !disabled && !isSelected
      ? hover({
          backgroundColor: colorUtils(theme.color.white)
            .darken(0.05)
            .hex()
        })
      : {
          backgroundColor: theme.color.basis1
        }
);

const PlayerBarWrapper = styled.div({
  display: 'flex',
  alignItems: 'center'
});

const Datum = styled.div(({ pad, theme }) => ({
  paddingRight: pad ? theme.spacing.small : 0
}));

const BarFinal = styled.div({
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'space-between'
});

const PlayerBarName = styled.div(
  ({ theme }) => ({
    flexBasis: 0,
    flexShrink: 1,
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: theme.fontWeight.demiBold
  }),
  ({ isScratched }) => (isScratched ? { textDecoration: 'line-through' } : {})
);

const Wrapper = styled.div(({ disabled, theme }) => ({
  marginTop: theme.spacing.tiny,
  opacity: disabled ? 0.3 : 1
}));

const defaultProps = {
  disabled: false,
  dropped: false,
  labels: [],
  isScratched: false,
  isSelected: false,
  isTeam: false,
  scheme: 'light'
};

const CruncherEntityFilter: React.FC<Props> = ({
  disabled,
  isScratched,
  labels,
  name,
  win,
  place,
  scheme,
  isSelected,
  onSelectEntity
}: Props) => (
  <Wrapper disabled={disabled}>
    <Tappable
      component={({ children, ...rest }) => (
        <EntityWrapper isSelected={isSelected} scheme={scheme} {...rest}>
          {children}
        </EntityWrapper>
      )}
      onClick={disabled ? undefined : onSelectEntity}
    >
      <PlayerBarWrapper>
        <PlayerBarName isScratched={isScratched}>{name}</PlayerBarName>
        {Array.isArray(labels) && labels.length > 0 && (
          <span>
            <PlayerLabels items={labels} />
          </span>
        )}
      </PlayerBarWrapper>
      <BarFinal>
        <Datum pad>
          <span>W </span>
          {win ? (
            <StarSymbol>
              <AnimateNumber format="0.00" number={win} />
            </StarSymbol>
          ) : (
            '\u2012'
          )}
        </Datum>
        <Datum>
          <span>P </span>
          {place ? (
            <StarSymbol>
              <AnimateNumber format="0.00" number={place} />
            </StarSymbol>
          ) : (
            '\u2012'
          )}
        </Datum>
      </BarFinal>
    </Tappable>
  </Wrapper>
);

CruncherEntityFilter.defaultProps = defaultProps;

export default React.memo(CruncherEntityFilter);
